import { useEffect, useRef, useState } from 'react'
import { loadGoogleMaps } from '../../lib/googleMaps'
import { stateAbbr } from '../../lib/states'

const KEY =
  import.meta.env.VITE_GOOGLE_PLACES_API_KEY ||
  import.meta.env.VITE_GOOGLE_MAPS_KEY

function extractParts(place) {
  const get = (type, short = false) => {
    const c = place.address_components?.find((x) => x.types.includes(type))
    return c ? (short ? c.short_name : c.long_name) : ''
  }
  const streetNumber = get('street_number')
  const route = get('route')
  return {
    addressLine1: [streetNumber, route].filter(Boolean).join(' '),
    city: get('locality') || get('sublocality') || get('postal_town'),
    state: get('administrative_area_level_1'),
    zip: get('postal_code'),
    country: get('country') || 'United States',
    lat: place.geometry?.location?.lat?.() ?? null,
    lng: place.geometry?.location?.lng?.() ?? null,
  }
}

function formatFullAddress({ addressLine1, city, state, zip }) {
  const abbr = stateAbbr(state) || state || ''
  const tail = [city, [abbr, zip].filter(Boolean).join(' ').trim()]
    .filter(Boolean)
    .join(', ')
  return [addressLine1, tail].filter(Boolean).join(', ')
}

export default function AddressAutocomplete({
  value,
  onChange,
  onLookup,
  id,
  hasError,
}) {
  const inputRef = useRef(null)
  const acRef = useRef(null)
  const [ready, setReady] = useState(
    () =>
      typeof window !== 'undefined' && !!window.google?.maps?.places,
  )

  useEffect(() => {
    if (!KEY) return
    let cancelled = false
    loadGoogleMaps(KEY).then((ok) => {
      // Double-check places actually attached before flipping ready, so a
      // wrongly-true result can never push us into constructing Autocomplete.
      if (!cancelled && ok && window.google?.maps?.places) setReady(true)
    })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!ready || !inputRef.current || acRef.current) return
    // Even if `ready` is somehow true, bail to the plain input rather than
    // throw when Maps was blocked and places never attached.
    if (!window.google?.maps?.places?.Autocomplete) return
    const g = window.google
    let listener
    try {
      // Try 'geocode' for ZIP predictions; this is a single try — if unsupported
      // in the future, revert to 'address'.
      let ac
      try {
        ac = new g.maps.places.Autocomplete(inputRef.current, {
          types: ['geocode'],
          componentRestrictions: { country: ['us'] },
          fields: ['address_components', 'geometry', 'formatted_address'],
        })
      } catch {
        ac = new g.maps.places.Autocomplete(inputRef.current, {
          types: ['address'],
          componentRestrictions: { country: ['us'] },
          fields: ['address_components', 'geometry', 'formatted_address'],
        })
      }
      acRef.current = ac
      listener = ac.addListener('place_changed', () => {
        const place = ac.getPlace()
        if (!place?.address_components) return
        const parsed = extractParts(place)
        if (onChange) onChange(parsed)
        if (onLookup) onLookup(parsed)
        // Override the input's displayed text to the full address while the
        // underlying form state keeps `addressLine1` as just the street.
        if (inputRef.current) {
          inputRef.current.value = formatFullAddress(parsed)
        }
      })
    } catch (err) {
      // Maps present but Autocomplete construction failed — leave the plain
      // <input> working. No throw escapes the component, so no ErrorBoundary.
      console.warn('Address autocomplete unavailable; using plain input.', err)
      acRef.current = null
      return
    }
    return () => {
      if (listener?.remove) listener.remove()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready])

  // Display the full formatted address when we have city/state/zip; fall back
  // to just the street when that's all we know. Keeps the form's underlying
  // addressLine1 as the street-only value.
  const initialDisplay =
    value?.city || value?.state || value?.zip
      ? formatFullAddress(value)
      : value?.addressLine1 || ''

  // Sync when value changes externally (e.g. late enrichment) and input isn't
  // focused, so we don't fight the user mid-keystroke.
  useEffect(() => {
    const el = inputRef.current
    if (!el) return
    if (document.activeElement === el) return
    const expected =
      value?.city || value?.state || value?.zip
        ? formatFullAddress(value)
        : value?.addressLine1 || ''
    if (el.value !== expected) el.value = expected
  }, [value?.addressLine1, value?.city, value?.state, value?.zip])

  return (
    <input
      ref={inputRef}
      id={id}
      type="text"
      className={`field-input${hasError ? ' has-error' : ''}`}
      placeholder={
        KEY ? 'Start typing an address…' : 'Street address'
      }
      defaultValue={initialDisplay}
      autoComplete="address-line1"
      data-lpignore="true"
      data-form-type="other"
      onChange={(e) => {
        if (onChange) onChange({ addressLine1: e.target.value })
      }}
    />
  )
}
